import { CircleHelp } from "lucide-react";
import { Popover } from "radix-ui";
import type { ReactNode } from "react";

import { cn } from "@/lib/cn";

/**
 * A small "?" that explains a word in plain English. Opens on tap or click
 * (hover tooltips don't work on phones), closes on a tap outside or Escape.
 */
export function InfoTip({
  term,
  children,
  className,
}: {
  /** The word explained; the button reads "What does {term} mean?". */
  term: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={`What does ${term} mean?`}
          className={cn(
            "inline-grid size-4 shrink-0 place-items-center rounded-full align-[-2px] text-subtle hover:text-brand focus-visible:text-brand",
            className,
          )}
        >
          <CircleHelp className="size-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="top"
          sideOffset={6}
          collisionPadding={12}
          className="z-50 max-w-[min(18rem,calc(100vw-24px))] rounded-lg border border-border bg-surface px-3 py-2 text-xs leading-relaxed text-fg shadow-card"
        >
          <span className="block font-semibold">{term}</span>
          <span className="block text-muted">{children}</span>
          <Popover.Arrow className="fill-surface" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
